import React, { useState, useEffect, useRef, useCallback } from 'react';
import mermaid from 'mermaid';
import DOMPurify from 'dompurify';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// DOS-261 / CN-007: sanitize Mermaid-rendered SVG before injecting via
// dangerouslySetInnerHTML. Mermaid runs user-supplied diagram syntax
// through its own parser, but the resulting SVG can still contain
// javascript: URLs in links or xlink:href references.
const MERMAID_SANITIZE_CONFIG = {
  USE_PROFILES: { svg: true, svgFilters: true },
  FORBID_TAGS: ['script', 'foreignObject'],
  FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onmouseout',
                'onfocus', 'onblur', 'onkeydown', 'onkeyup', 'onkeypress'],
};
const sanitizeMermaidSvg = (svg) => svg ? DOMPurify.sanitize(svg, MERMAID_SANITIZE_CONFIG) : '';

// Initialize mermaid with default config
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'strict',  // DOS-261 / CN-007: disable click callbacks + javascript: URLs
  fontFamily: 'system-ui, sans-serif',
});

/**
 * MermaidArtifactPreview Component
 *
 * Renders Mermaid diagram artifacts with:
 * - Live rendered diagram preview
 * - Toggle between code and preview views
 * - Export as PNG/SVG
 * - Theme support (light/dark)
 * - Error handling for invalid syntax
 */
function MermaidArtifactPreview({ content, title, isEditing = false, onContentChange }) {
  const [viewMode, setViewMode] = useState('preview'); // 'preview', 'code', or 'edit'
  const [copied, setCopied] = useState(false);
  const [renderedSvg, setRenderedSvg] = useState('');
  const [renderError, setRenderError] = useState(null);
  const [diagramTheme, setDiagramTheme] = useState('default'); // 'default', 'dark', 'forest', 'neutral'
  const containerRef = useRef(null);
  const textareaRef = useRef(null);
  const renderIdRef = useRef(0);

  // Switch to edit view when editing starts
  useEffect(() => {
    if (isEditing) {
      setViewMode('edit');
    } else if (viewMode === 'edit') {
      setViewMode('preview');
    }
  }, [isEditing]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && viewMode === 'edit' && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing, viewMode]);

  // Handle content changes in edit mode
  const handleChange = (e) => {
    if (onContentChange) {
      onContentChange(e.target.value);
    }
  };

  // Calculate line numbers for editor
  const lineCount = (content || '').split('\n').length;

  // Render mermaid diagram
  const renderDiagram = useCallback(async () => {
    if (!content || viewMode === 'code') return;

    try {
      setRenderError(null);
      renderIdRef.current += 1;
      const id = `mermaid-${renderIdRef.current}`;

      // Update mermaid theme
      mermaid.initialize({
        startOnLoad: false,
        theme: diagramTheme,
        securityLevel: 'strict',  // DOS-261 / CN-007: disable click callbacks + javascript: URLs
        fontFamily: 'system-ui, sans-serif',
      });

      // Render the diagram
      const { svg } = await mermaid.render(id, content.trim());
      setRenderedSvg(svg);
    } catch (error) {
      console.error('Mermaid rendering error:', error);
      setRenderError(error.message || 'Failed to render diagram');
      setRenderedSvg('');
    }
  }, [content, diagramTheme, viewMode]);

  // Re-render when content or theme changes
  useEffect(() => {
    const timer = setTimeout(() => {
      renderDiagram();
    }, 300); // Debounce rendering

    return () => clearTimeout(timer);
  }, [renderDiagram]);

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Export diagram as PNG
  const handleExportPng = async () => {
    if (!renderedSvg) return;

    try {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Create an image from the SVG
      const svgBlob = new Blob([renderedSvg], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        // Set canvas size with padding
        const padding = 20;
        canvas.width = img.width + (padding * 2);
        canvas.height = img.height + (padding * 2);

        // Fill background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw the image
        ctx.drawImage(img, padding, padding);

        // Convert to PNG and download
        canvas.toBlob((blob) => {
          const downloadUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `${title || 'diagram'}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(downloadUrl);
        }, 'image/png');

        URL.revokeObjectURL(url);
      };
      img.src = url;
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Export diagram as SVG
  const handleExportSvg = () => {
    if (!renderedSvg) return;

    const blob = new Blob([renderedSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title || 'diagram'}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Theme options
  const themes = [
    { id: 'default', label: 'Default' },
    { id: 'dark', label: 'Dark' },
    { id: 'forest', label: 'Forest' },
    { id: 'neutral', label: 'Neutral' },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar with view toggle */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        {/* View mode toggle */}
        <div className="flex items-center bg-gray-700 rounded-lg p-0.5">
          {isEditing && (
            <button
              onClick={() => setViewMode('edit')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'edit'
                  ? 'bg-gray-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </span>
            </button>
          )}
          <button
            onClick={() => setViewMode('preview')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              viewMode === 'preview'
                ? 'bg-gray-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Preview
            </span>
          </button>
          <button
            onClick={() => setViewMode('code')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              viewMode === 'code'
                ? 'bg-gray-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              Code
            </span>
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1">
          {/* Theme selector (preview mode only) */}
          {viewMode === 'preview' && (
            <div className="flex items-center gap-1 mr-2 border-r border-gray-600 pr-2">
              <span className="text-xs text-gray-400 mr-1">Theme:</span>
              <select
                value={diagramTheme}
                onChange={(e) => setDiagramTheme(e.target.value)}
                className="bg-gray-700 text-gray-300 text-xs rounded-sm px-2 py-1 border-none focus:ring-1 focus:ring-blue-500"
              >
                {themes.map((theme) => (
                  <option key={theme.id} value={theme.id}>
                    {theme.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Export buttons (preview mode only) */}
          {viewMode === 'preview' && renderedSvg && (
            <div className="flex items-center gap-1 mr-2 border-r border-gray-600 pr-2">
              <button
                onClick={handleExportPng}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white
                  hover:bg-gray-700 rounded transition-colors"
                title="Export as PNG"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                PNG
              </button>
              <button
                onClick={handleExportSvg}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white
                  hover:bg-gray-700 rounded transition-colors"
                title="Export as SVG"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                SVG
              </button>
            </div>
          )}

          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-400 hover:text-white
              hover:bg-gray-700 rounded transition-colors"
            title={copied ? 'Copied!' : 'Copy Mermaid code'}
          >
            {copied ? (
              <>
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-green-500">Copied!</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'edit' ? (
          // Split view - Editor on left, Preview on right
          <div className="flex h-full">
            {/* Code editor panel */}
            <div className="w-1/2 h-full overflow-auto bg-gray-900 border-r border-gray-700">
              <div className="flex h-full">
                {/* Line numbers column */}
                <div className="shrink-0 py-4 pl-4 pr-0 text-right text-gray-500 font-mono text-sm select-none"
                  style={{ minWidth: '3em', lineHeight: '1.6' }}
                >
                  {Array.from({ length: lineCount }, (_, i) => (
                    <div key={i + 1}>{i + 1}</div>
                  ))}
                </div>
                {/* Editable textarea */}
                <textarea
                  ref={textareaRef}
                  value={content || ''}
                  onChange={handleChange}
                  className="flex-1 bg-transparent text-gray-100 font-mono text-sm p-4 pl-4 resize-none focus:outline-hidden"
                  style={{
                    lineHeight: '1.6',
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    tabSize: 2,
                  }}
                  spellCheck={false}
                  placeholder="graph TD&#10;    A[Start] --> B[End]"
                />
              </div>
            </div>
            {/* Live preview panel */}
            <div className="w-1/2 h-full p-4 bg-white dark:bg-gray-100 overflow-auto">
              {renderError ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center p-4">
                    <svg className="w-12 h-12 mx-auto text-red-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-red-600 font-medium">Diagram Error</p>
                    <p className="text-sm text-gray-500 mt-1 max-w-md">{renderError}</p>
                  </div>
                </div>
              ) : (
                <div
                  className="mermaid-container flex items-center justify-center min-h-full"
                  dangerouslySetInnerHTML={{ __html: sanitizeMermaidSvg(renderedSvg) }}
                />
              )}
            </div>
          </div>
        ) : viewMode === 'preview' ? (
          // Live rendered diagram
          <div
            ref={containerRef}
            className="h-full overflow-auto p-6 bg-white dark:bg-gray-100 flex items-center justify-center"
          >
            {renderError ? (
              <div className="text-center p-6 bg-red-50 rounded-lg max-w-md">
                <svg className="w-12 h-12 mx-auto text-red-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-red-600 font-medium mb-2">Failed to render diagram</p>
                <p className="text-sm text-red-500">{renderError}</p>
                <button
                  onClick={() => setViewMode('code')}
                  className="mt-4 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm transition-colors"
                >
                  View Source Code
                </button>
              </div>
            ) : (
              <div
                className="mermaid-container"
                dangerouslySetInnerHTML={{ __html: sanitizeMermaidSvg(renderedSvg) }}
              />
            )}
          </div>
        ) : (
          // Code view with syntax highlighting
          <div className="h-full overflow-auto bg-gray-900">
            <SyntaxHighlighter
              language="markdown"
              style={oneDark}
              showLineNumbers={true}
              wrapLines={true}
              lineNumberStyle={{
                minWidth: '3em',
                paddingRight: '1em',
                textAlign: 'right',
                color: '#6b7280',
                userSelect: 'none',
              }}
              customStyle={{
                margin: 0,
                padding: '1rem',
                fontSize: '0.875rem',
                lineHeight: '1.6',
                background: 'transparent',
                minHeight: '100%',
              }}
              codeTagProps={{
                style: {
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                }
              }}
            >
              {content || '# No Mermaid content'}
            </SyntaxHighlighter>
          </div>
        )}
      </div>

      {/* Preview info bar */}
      {viewMode === 'preview' && (
        <div className="px-4 py-2 bg-gray-800 border-t border-gray-700 text-xs text-gray-400 flex items-center gap-2">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span>Mermaid diagram • Theme: {themes.find(t => t.id === diagramTheme)?.label} • Export: PNG, SVG</span>
        </div>
      )}

      {/* Mermaid container styles */}
      <style>{`
        .mermaid-container svg {
          max-width: 100%;
          height: auto;
        }
        .mermaid-container .node rect,
        .mermaid-container .node circle,
        .mermaid-container .node polygon {
          stroke-width: 2px;
        }
      `}</style>
    </div>
  );
}

export default MermaidArtifactPreview;
